import React, { useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGlobe, FiKey, FiSettings, FiCheck, FiX, FiExternalLink, FiCode, FiShield, FiZap, FiUsers, FiBarChart3 } = FiIcons;

const Integrations = () => {
    const [apiSettings, setApiSettings] = useState({
        enabled: true,
        rateLimitPerHour: 1000,
        requireAuthentication: true,
        allowCors: true,
        webhooksEnabled: true
    });

    const [integrations, setIntegrations] = useState([
        {
            id: 1,
            name: 'Google Analytics',
            category: 'Analytics',
            description: 'Track user behavior and website performance',
            icon: FiBarChart3,
            enabled: true,
            configured: true,
            status: 'Connected'
        },
        {
            id: 2,
            name: 'Zapier',
            category: 'Automation',
            description: 'Connect with 3000+ apps and automate workflows',
            icon: FiZap,
            enabled: true,
            configured: false,
            status: 'Available'
        },
        {
            id: 3,
            name: 'Microsoft 365',
            category: 'SSO',
            description: 'Single sign-on with Microsoft accounts',
            icon: FiUsers,
            enabled: false,
            configured: false,
            status: 'Available'
        },
        {
            id: 4,
            name: 'Google SSO',
            category: 'SSO',
            description: 'Single sign-on with Google accounts',
            icon: FiShield,
            enabled: false,
            configured: false,
            status: 'Available'
        },
        {
            id: 5,
            name: 'QuickBooks Online',
            category: 'Accounting',
            description: 'Sync expenses with QuickBooks accounting software',
            icon: FiGlobe,
            enabled: false,
            configured: false,
            status: 'Available'
        },
        {
            id: 6,
            name: 'Slack',
            category: 'Communication',
            description: 'Send notifications and updates to Slack channels',
            icon: FiCode,
            enabled: false,
            configured: false,
            status: 'Available'
        }
    ]);

    const [seoSettings, setSeoSettings] = useState({
        siteTitle: 'Softvative Expense Tracker - Professional Expense Management',
        metaDescription: 'Track expenses, manage budgets, and gain insights with our powerful expense tracking platform.',
        keywords: 'expense tracker, budget management, financial analytics, business expenses',
        ogTitle: 'Softvative Expense Tracker',
        ogDescription: 'Professional expense tracking and budget management platform',
        twitterCard: 'summary_large_image'
    });

    const [captchaSettings, setCaptchaSettings] = useState({
        enabled: true,
        provider: 'recaptcha',
        siteKey: '',
        secretKey: '',
        threshold: 0.5
    });

    const categories = ['All', 'Analytics', 'Automation', 'SSO', 'Accounting', 'Communication'];
    const [selectedCategory, setSelectedCategory] = useState('All');

    const filteredIntegrations = integrations.filter(integration =>
        selectedCategory === 'All' || integration.category === selectedCategory
    );

    const toggleIntegration = (integrationId) => {
        setIntegrations(integrations.map(integration =>
            integration.id === integrationId
                ? { ...integration, enabled: !integration.enabled }
                : integration
        ));

        const integration = integrations.find(i => i.id === integrationId);
        toast.success(`${integration.name} ${integration.enabled ? 'disabled' : 'enabled'}`);
    };

    const configureIntegration = (integrationId) => {
        const integration = integrations.find(i => i.id === integrationId);
        toast.info(`Opening ${integration.name} configuration...`);
    };

    const saveApiSettings = () => {
        toast.success('API settings saved successfully!');
    };

    const saveSeoSettings = () => {
        toast.success('SEO settings saved successfully!');
    };

    const saveCaptchaSettings = () => {
        toast.success('CAPTCHA settings saved successfully!');
    };

    const generateApiKey = () => {
        const newKey = 'sk_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        toast.success(`New API key generated: ${newKey}`);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-8"
                >
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Integrations & APIs
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300">
                        Manage third-party integrations and API settings
                    </p>
                </motion.div>

                <div className="space-y-8">
                    {/* API Settings */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center">
                                <SafeIcon icon={FiKey} className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3" />
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    API Configuration
                                </h2>
                            </div>
                            <button
                                onClick={generateApiKey}
                                className="px-4 py-2 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-lg hover:from-primary-700 hover:to-accent-700 transition-all duration-200"
                            >
                                Generate New Key
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">Enable API Access</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">Allow external API access</p>
                                    </div>
                                    <button
                                        onClick={() => setApiSettings({ ...apiSettings, enabled: !apiSettings.enabled })}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${apiSettings.enabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                                            }`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${apiSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                                                }`}
                                        />
                                    </button>
                                </div>

                                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">Webhooks</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">Enable webhook notifications</p>
                                    </div>
                                    <button
                                        onClick={() => setApiSettings({ ...apiSettings, webhooksEnabled: !apiSettings.webhooksEnabled })}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${apiSettings.webhooksEnabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                                            }`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${apiSettings.webhooksEnabled ? 'translate-x-6' : 'translate-x-1'
                                                }`}
                                        />
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Rate Limit (per hour)
                                    </label>
                                    <input
                                        type="number"
                                        value={apiSettings.rateLimitPerHour}
                                        onChange={(e) => setApiSettings({ ...apiSettings, rateLimitPerHour: parseInt(e.target.value) })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                </div>

                                <button
                                    onClick={saveApiSettings}
                                    className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                >
                                    Save API Settings
                                </button>
                            </div>
                        </div>
                    </motion.div>

                    {/* Third-party Integrations */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center">
                                <SafeIcon icon={FiGlobe} className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3" />
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Third-party Integrations
                                </h2>
                            </div>

                            {/* Category Filter */}
                            <div className="flex space-x-2">
                                {categories.map(category => (
                                    <button
                                        key={category}
                                        onClick={() => setSelectedCategory(category)}
                                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${selectedCategory === category
                                                ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                    >
                                        {category}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredIntegrations.map((integration, index) => (
                                <motion.div
                                    key={integration.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center">
                                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center mr-3">
                                                <SafeIcon icon={integration.icon} className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-gray-900 dark:text-white">
                                                    {integration.name}
                                                </h3>
                                                <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                                                    {integration.category}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => toggleIntegration(integration.id)}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${integration.enabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                                                }`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${integration.enabled ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                            />
                                        </button>
                                    </div>

                                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                                        {integration.description}
                                    </p>

                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className={`w-2 h-2 rounded-full mr-2 ${integration.status === 'Connected' ? 'bg-green-500' : 'bg-gray-400'
                                                }`} />
                                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                                {integration.status}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => configureIntegration(integration.id)}
                                            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                                        >
                                            Configure
                                        </button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* SEO Settings */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                    >
                        <div className="flex items-center mb-6">
                            <SafeIcon icon={FiBarChart3} className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3" />
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                SEO & Marketing Settings
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Site Title
                                </label>
                                <input
                                    type="text"
                                    value={seoSettings.siteTitle}
                                    onChange={(e) => setSeoSettings({ ...seoSettings, siteTitle: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Keywords
                                </label>
                                <input
                                    type="text"
                                    value={seoSettings.keywords}
                                    onChange={(e) => setSeoSettings({ ...seoSettings, keywords: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Meta Description
                                </label>
                                <textarea
                                    rows={3}
                                    value={seoSettings.metaDescription}
                                    onChange={(e) => setSeoSettings({ ...seoSettings, metaDescription: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div className="md:col-span-2 flex justify-end">
                                <button
                                    onClick={saveSeoSettings}
                                    className="px-4 py-2 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-lg hover:from-primary-700 hover:to-accent-700 transition-all duration-200"
                                >
                                    Save SEO Settings
                                </button>
                            </div>
                        </div>
                    </motion.div>

                    {/* CAPTCHA Settings */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                    >
                        <div className="flex items-center mb-6">
                            <SafeIcon icon={FiShield} className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3" />
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                CAPTCHA Security
                            </h2>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">Enable CAPTCHA</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">Protect login page with CAPTCHA</p>
                                </div>
                                <button
                                    onClick={() => setCaptchaSettings({ ...captchaSettings, enabled: !captchaSettings.enabled })}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${captchaSettings.enabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                                        }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${captchaSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                            </div>

                            {captchaSettings.enabled && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Provider
                                        </label>
                                        <select
                                            value={captchaSettings.provider}
                                            onChange={(e) => setCaptchaSettings({ ...captchaSettings, provider: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        >
                                            <option value="recaptcha">Google reCAPTCHA</option>
                                            <option value="hcaptcha">hCaptcha</option>
                                            <option value="turnstile">Cloudflare Turnstile</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Threshold
                                        </label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            max="1"
                                            value={captchaSettings.threshold}
                                            onChange={(e) => setCaptchaSettings({ ...captchaSettings, threshold: parseFloat(e.target.value) })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div>

                                    <div className="md:col-span-2 flex justify-end">
                                        <button
                                            onClick={saveCaptchaSettings}
                                            className="px-4 py-2 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-lg hover:from-primary-700 hover:to-accent-700 transition-all duration-200"
                                        >
                                            Save CAPTCHA Settings
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default Integrations;